import React, { useState } from 'react';
import {
  AppContent,
  Navigate,
  Outlet,
  Route,
  Routes,
  Title,
} from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import { ScheduleVerticalNav } from '@load-balancing/components/schedule-vertical-nav';
import { ScheduleDashboardPage } from '@load-balancing/pages/organization/schedule-dashboard-page';
import { ScheduleReviewPage } from '@load-balancing/pages/organization/schedule-review-page';
import { ScheduleTeamReviewPage } from '@load-balancing/pages/organization/schedule-team-review-page';

export function ScheduleRoutes() {
  // Controls the ScheduleVerticalNav chrome — full-canvas pages (e.g. Review) hide it.
  const [showMenu, setShowMenu] = useState(false);

  return (
    // Plain Routes (not SentryRoutes) — avoids last-writer-wins transaction
    // name conflicts on nested sub-routers.
    //
    // The Dashboard is service-line-agnostic (one grouped view across every service line),
    // so it carries no `:serviceLineId`. The per-service-line pages (Review, Team) keep it,
    // moved AFTER the page segment now that the dashboard owns the bare `/dashboard` path.
    <Routes>
      <Route
        element={
          <ScheduleVerticalNav hideMenu={!showMenu}>
            <Outlet />
          </ScheduleVerticalNav>
        }
      >
        <Route
          path="/"
          element={<Navigate to="/works/schedule/dashboard" replace />}
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute
              menuItem="schedule"
              page={ScheduleDashboardPage}
              onRender={() => setShowMenu(false)}
            />
          }
        />
        <Route
          // Deep-linkable per (balancer run, organization unit), scoped by service
          // line. The dashboard→review nav lands with the real dashboard query
          // (WORK-5990).
          path="/dashboard/:serviceLineId/review/:runId/:organizationUnitId"
          element={
            <PrivateRoute
              menuItem="schedule"
              page={ScheduleReviewPage}
              onRender={() => setShowMenu(false)}
            />
          }
        />
        <Route
          // Read-only roster review before generating a balanced schedule. Scoped to a
          // (service line, scheduling period): the period is structural — it drives FTE
          // hours and anchors the "current term" selection on the period's start.
          // Follows the per-service-line convention — :serviceLineId after the page
          // segment, mirroring the review route now that the dashboard owns bare `/dashboard`.
          path="/dashboard/:serviceLineId/team-review/:schedulingPeriodId"
          element={
            <PrivateRoute
              menuItem="schedule"
              page={ScheduleTeamReviewPage}
              onRender={() => setShowMenu(false)}
            />
          }
        />
        <Route
          path="/team/:serviceLineId"
          element={
            <PrivateRoute
              menuItem="schedule"
              page={ScheduleComingSoonPage}
              onRender={() => setShowMenu(false)}
            />
          }
        />
        <Route
          path="*"
          element={
            <PrivateRoute
              menuItem="schedule"
              page={ScheduleComingSoonPage}
              onRender={() => setShowMenu(false)}
            />
          }
        />
      </Route>
    </Routes>
  );
}

// Catch-all that keeps the ScheduleVerticalNav chrome visible when a
// nav item routes to a page that hasn't been built yet (e.g. Team).
function ScheduleComingSoonPage() {
  return (
    <AppContent>
      <Title size="h1">Coming soon</Title>
    </AppContent>
  );
}
